import React, { useCallback, useMemo } from 'react';
import { AutoSizeTextarea } from 'web/src/administration/components/worksheet/dynamic-grid/auto-size-textarea';
import { createUseStyles } from 'react-jss';
import { DynamicGridCell } from 'web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-cell';
import { DynamicGridHeaderCell } from 'web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-header-cell';
import { DynamicGridRow } from 'web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-row';
import { formatWithCommas } from '@packages/ui/worksheet/utils';
import { FormElementGridCellAnswer } from '@packages/models/api';
import { Platform } from 'react-native';
import { Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

interface ColumnType {
    worksheetElementGridId: string;
    columnLabel: string;
    columnOrder: number;
    defaultValue: string;
    subColumns?: [];
    isRequired: boolean;
}

interface SubRowType {
    subRowLabel: string;
    rowOrder: number;
    columns: ColumnType[];
}

export interface SubRowGridType {
    rowLabel: string;
    rowOrder: number;
    subRows: SubRowType[];
    columns: [];
}

interface PressureTestGridProps {
    cornerHeader: string;
    onChange?: (worksheetElementGridId: string, value: string) => void;
    onBlur?: (worksheetElementGridId: string) => void;
    readOnly?: boolean;
    rows: SubRowGridType[];
    answers?: FormElementGridCellAnswer[];
    isCellInvalid?: (worksheetElementGridId: string) => boolean;
}

const useStyles = createUseStyles({
    container: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        overflowX: 'auto',
    },
    table: {
        height: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        borderSpacing: 0,
        minWidth: '100%',
        border: `1px solid ${colors.grayThree}`,
    },
    headerBorderRight: {
        borderRight: `2px solid ${colors.grayThree}`,
    },
    borderLeft: {
        borderLeft: `1px solid ${colors.grayThree}`,
    },
    cell: {
        minWidth: 90,
        width: 90,
    },
    tbody: {
        '& > tr:nth-child(odd)': {
            borderTop: `2px solid ${colors.grayThree}`,
        },
        '& > tr:nth-child(even)': {
            borderTop: `1px solid ${colors.grayThree}`,
        },
    },
    headerContent: {
        padding: '14px',
        display: 'block',
    },
});

export function PressureTestGrid({
    cornerHeader = '',
    onChange,
    onBlur,
    readOnly,
    rows,
    answers = [],
    isCellInvalid,
}: PressureTestGridProps) {
    const classes = useStyles();

    const focusTextarea = useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
        const textarea = e.currentTarget.querySelector('textarea');
        if (textarea && !textarea.disabled) textarea.focus();
    }, []);

    const headerColumns = useMemo(() => rows?.[0]?.subRows?.[0]?.columns ?? [], [rows]);

    if (!rows?.length || !headerColumns.length) {
        return null;
    }

    if (Platform.OS !== 'web') {
        return null;
    }

    return (
        <div className={classes.container}>
            <table className={classes.table}>
                <thead>
                    <DynamicGridRow>
                        <DynamicGridHeaderCell
                            className={`${classes.cell} ${classes.headerBorderRight}`}
                            contentClassName={classes.headerContent}
                        >
                            <Typography style={{ fontWeight: 500 }} color={readOnly ? 'grayFive' : 'textDarkPrimary'}>
                                {cornerHeader}
                            </Typography>
                        </DynamicGridHeaderCell>

                        <DynamicGridHeaderCell className={classes.cell} contentClassName={classes.headerContent}>
                            <Typography style={{ fontWeight: 500 }} color={readOnly ? 'grayFive' : 'textDarkPrimary'}>
                                Throttle opening angle
                            </Typography>
                        </DynamicGridHeaderCell>

                        {headerColumns.map((column) => (
                            <DynamicGridHeaderCell
                                key={`col-header-${column.columnOrder}`}
                                className={`${classes.cell} ${classes.borderLeft}`}
                                contentClassName={classes.headerContent}
                            >
                                <Typography
                                    style={{ fontWeight: 500 }}
                                    color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                >
                                    {column.columnLabel}
                                    {column.isRequired && <Typography color="redOne"> *</Typography>}
                                </Typography>
                            </DynamicGridHeaderCell>
                        ))}
                    </DynamicGridRow>
                </thead>

                <tbody className={classes.tbody}>
                    {rows.flatMap((row) => {
                        const rowSpan = row.subRows.length;

                        return row.subRows.map((subRow, subRowIndex: number) => (
                            <DynamicGridRow key={`row-${row.rowOrder}-sub-row-${subRow.subRowLabel}`}>
                                {subRowIndex === 0 && (
                                    <DynamicGridHeaderCell
                                        rowSpan={rowSpan}
                                        className={classes.headerBorderRight}
                                        contentClassName={classes.headerContent}
                                    >
                                        <Typography
                                            style={{ fontWeight: 500 }}
                                            color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                        >
                                            {row.rowLabel}
                                        </Typography>
                                    </DynamicGridHeaderCell>
                                )}

                                <DynamicGridHeaderCell
                                    className={classes.borderLeft}
                                    contentClassName={classes.headerContent}
                                >
                                    <Typography
                                        numberOfLines={1}
                                        style={{ fontWeight: 500 }}
                                        color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                    >
                                        {subRow.subRowLabel}
                                    </Typography>
                                </DynamicGridHeaderCell>

                                {subRow.columns.map((column) => {
                                    const cellKey = `row-${row.rowOrder}-sub-row-${subRow.subRowLabel}-col-${column.columnOrder}`;
                                    const isInvalid = isCellInvalid?.(column.worksheetElementGridId);
                                    const value =
                                        answers.find(
                                            (answer) => answer.worksheetElementGridId === column.worksheetElementGridId
                                        )?.value ?? '';

                                    return (
                                        <DynamicGridCell
                                            key={`cell-${cellKey}`}
                                            onClick={focusTextarea}
                                            className={classes.borderLeft}
                                            invalid={isInvalid}
                                            readOnly={readOnly}
                                        >
                                            <AutoSizeTextarea
                                                id={`input-${column.worksheetElementGridId}`}
                                                value={formatWithCommas(value)}
                                                disabled={readOnly}
                                                required={column.isRequired}
                                                onChange={(value) => {
                                                    onChange?.(column.worksheetElementGridId, value);
                                                }}
                                                onBlur={() => {
                                                    onBlur?.(column.worksheetElementGridId);
                                                }}
                                                placeholder={isInvalid ? 'Required' : ''}
                                            />
                                        </DynamicGridCell>
                                    );
                                })}
                            </DynamicGridRow>
                        ));
                    })}
                </tbody>
            </table>
        </div>
    );
}
